import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Heart, Coffee, Battery, BookOpen } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
// Image Constants
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/dewVrkp2eyig2sA64yZibw/ChatGPT_Image_7_ene_2026,_04_08_05.png';
const BONUS_1 = 'https://cdn.magicpatterns.com/uploads/mfKJZzymBMZvHAWb2tBB96/ChatGPT_Image_7_ene_2026,_05_05_26.png';
const BONUS_2 = 'https://cdn.magicpatterns.com/uploads/imBxFb8w7YvafTNbm8GHVy/ChatGPT_Image_7_ene_2026,_05_05_30.png';
const BONUS_3 = 'https://cdn.magicpatterns.com/uploads/5rsK6F3pniDQrqTkeFQ1Mq/ChatGPT_Image_7_ene_2026,_05_05_37.png';
const CHECKOUT_LINK = 'https://checkout2.rosetadigital.com/cart/46947956850866:1';
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handlePurchase = () => {
    window.location.href = CHECKOUT_LINK;
  };
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-[#F5F1E8] font-serif text-[#4A4A4A] overflow-x-hidden" data-id="element-17">
      <PurchaseNotification data-id="element-18" />

      {/* Hero Section */}
      <header className="relative pt-8 pb-16 px-4 md:pt-16 md:pb-24" data-id="element-19">
        <div className="max-w-4xl mx-auto text-center" data-id="element-20">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="mb-8" data-id="element-21">
            <span className="inline-block py-1 px-3 rounded-full bg-[#D4915D]/10 text-[#D4915D] text-sm font-bold tracking-wider mb-4 uppercase" data-id="element-22">
              Para mujeres cansadas
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 leading-tight" data-id="element-23">
              CÓMO SENTIRTE MEJOR <br data-id="element-24" />
              <span className="text-[#D4915D] italic" data-id="element-25">SIN CAMBIAR NADA</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 italic max-w-2xl mx-auto" data-id="element-26">
              Alivio emocional para cuando seguís funcionando, pero por dentro
              ya no das más.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="relative max-w-md mx-auto mb-12 cursor-pointer group" onClick={scrollToPurchase} data-id="element-27">
            <div className="absolute inset-0 bg-[#D4915D] rounded-lg transform rotate-3 opacity-20 group-hover:rotate-6 transition-transform duration-500" data-id="element-28"></div>
            <img src={MAIN_IMAGE} alt="Libro: Cómo Sentirte Mejor Sin Cambiar Nada" className="relative rounded-lg shadow-2xl w-full transform group-hover:-translate-y-2 transition-transform duration-500" data-id="element-29" />
          </motion.div>

          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={scrollToPurchase} className="bg-[#D4915D] text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 mx-auto" data-id="element-30">
            Quiero sentir alivio ahora
            <ArrowRight className="w-5 h-5" data-id="element-31" />
          </motion.button>
        </div>
      </header>

      {/* Temporal Urgency Section - NEW */}
      <section className="py-8 px-4 bg-[#D4915D]/5 border-y border-[#D4915D]/20" data-id="element-32">
        <div className="max-w-3xl mx-auto text-center" data-id="element-33">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-34">
            <p className="text-sm uppercase tracking-wider text-[#D4915D] font-bold mb-2" data-id="element-35">
              Enero 2026
            </p>
            <p className="text-lg md:text-xl text-gray-700 font-serif" data-id="element-36">
              No empieces otro año arrastrando el cansancio del anterior.{' '}
              <span className="text-[#D4915D] font-bold" data-id="element-37">
                Este es tu momento para aflojar antes de que el año te exija
                más.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Copy Section 1: The Problem */}
      <section className="py-16 px-4 bg-white/50" data-id="element-38">
        <div className="max-w-2xl mx-auto space-y-8 text-lg leading-relaxed" data-id="element-39">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-40">
            <p className="font-bold text-xl mb-6" data-id="element-41">Seguís. Cumplís. Resolvés.</p>
            <p data-id="element-42">Desde afuera, todo parece estar bien.</p>
            <p data-id="element-43">Pero por dentro, el cansancio ya no se va solo con descanso.</p>
            <p data-id="element-44">
              No es sueño. No es falta de ganas. Es un agotamiento que se queda,
              incluso cuando todo "funciona".
            </p>
            <p className="font-medium text-[#D4915D]" data-id="element-45">
              Si esto te pasa, no estás sola. Y no necesitás cambiar nada de tu
              vida para sentirte mejor.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#F5F1E8] p-8 rounded-xl border border-[#E5E0D8] my-12" data-id="element-46">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3" data-id="element-47">
              <Battery className="text-[#D4915D]" data-id="element-48" />
              El cansancio que no se ve
            </h2>
            <p className="mb-4" data-id="element-49">
              Hay un tipo de cansancio del que casi no se habla. No se nota. No
              se explica fácil.
            </p>
            <p className="mb-4" data-id="element-50">
              Es ese agotamiento interno que te acompaña todo el día, aunque
              sigas cumpliendo, trabajando, cuidando y resolviendo.
            </p>
            <p className="italic" data-id="element-51">
              Te levantás cansada. Seguís. Terminás el día vacía.
            </p>
            <p className="mt-4 font-bold" data-id="element-52">
              Y lo más pesado no es el cansancio en sí, sino la culpa por
              sentirlo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Copy Section 2: The Solution */}
      <section className="py-16 px-4" data-id="element-53">
        <div className="max-w-2xl mx-auto space-y-8 text-lg leading-relaxed" data-id="element-54">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-55">
            <h2 className="text-3xl font-bold text-center mb-8" data-id="element-56">
              Cuando incluso la autoayuda cansa
            </h2>
            <p data-id="element-57">Probaste descansar. Probaste leer. Probaste motivarte.</p>
            <p data-id="element-58">Y aun así, algo no se afloja.</p>
            <p data-id="element-59">
              Porque muchas soluciones te piden más: más esfuerzo, más cambio,
              más trabajo personal.
            </p>
            <p className="text-xl font-bold text-center py-6" data-id="element-60">
              Pero… ¿y si el problema no es que falte algo?
              <br data-id="element-61" />
              ¿Y si lo que sobra es exigencia?
            </p>
            <p className="text-center text-[#D4915D] font-bold text-xl" data-id="element-62">
              No necesitás motivación. Necesitás alivio.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 my-12" data-id="element-63">
            <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-64">
              <h3 className="font-bold text-lg mb-4 text-red-400" data-id="element-65">
                Lo que NO es este libro
              </h3>
              <ul className="space-y-3" data-id="element-66">
                <li className="flex items-start gap-2" data-id="element-67">
                  <span className="text-red-400 font-bold" data-id="element-68">×</span> No te dice
                  qué hacer
                </li>
                <li className="flex items-start gap-2" data-id="element-69">
                  <span className="text-red-400 font-bold" data-id="element-70">×</span> No te empuja
                </li>
                <li className="flex items-start gap-2" data-id="element-71">
                  <span className="text-red-400 font-bold" data-id="element-72">×</span> No te exige
                  mejorar
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-[#D4915D]/20" data-id="element-73">
              <h3 className="font-bold text-lg mb-4 text-[#D4915D]" data-id="element-74">
                Lo que SÍ es
              </h3>
              <ul className="space-y-3" data-id="element-75">
                <li className="flex items-start gap-2" data-id="element-76">
                  <Check className="w-5 h-5 text-[#D4915D]" data-id="element-77" /> Una guía para
                  acompañarte
                </li>
                <li className="flex items-start gap-2" data-id="element-78">
                  <Check className="w-5 h-5 text-[#D4915D]" data-id="element-79" /> Un espacio de
                  validación
                </li>
                <li className="flex items-start gap-2" data-id="element-80">
                  <Check className="w-5 h-5 text-[#D4915D]" data-id="element-81" /> Alivio real sin
                  exigencias
                </li>
              </ul>
            </div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-82">
            <p className="mb-6" data-id="element-83">
              A través de un lenguaje cercano y humano, te ayuda a:
            </p>
            <ul className="space-y-4 mb-8" data-id="element-84">
              {['Entender qué te pasa', 'Ponerle nombre a ese agotamiento silencioso', 'Bajar la exigencia interna', 'Encontrar pequeñas pausas que alivian de verdad'].map((item, i) => <li key={i} className="flex items-center gap-3 bg-white/60 p-3 rounded-lg" data-id="element-85">
                  <Heart className="w-5 h-5 text-[#D4915D] fill-[#D4915D]" data-id="element-86" />
                  <span data-id="element-87">{item}</span>
                </li>)}
            </ul>
            <p className="font-bold text-center" data-id="element-88">
              Todo eso sin cambiar quién sos y sin transformar tu vida.
            </p>
          </motion.div>

          <div className="text-center pt-8" data-id="element-89">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToPurchase} className="bg-[#D4915D] text-white px-8 py-3 rounded-full text-lg font-bold shadow-md hover:shadow-lg transition-all" data-id="element-90">
              Necesito leer esto
            </motion.button>
          </div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-16 px-4 bg-[#EAE4D9]" data-id="element-91">
        <div className="max-w-3xl mx-auto" data-id="element-92">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12" data-id="element-93">
            <h2 className="text-3xl font-bold mb-6" data-id="element-94">
              El verdadero problema <br data-id="element-95" />
              <span className="text-xl font-normal" data-id="element-96">
                (y por qué no es tu culpa)
              </span>
            </h2>
            <p className="text-lg mb-6" data-id="element-97">
              No estás cansada porque seas débil. No estás cansada porque no te
              quieras lo suficiente.
            </p>
            <p className="text-xl font-bold text-[#D4915D]" data-id="element-98">
              Estás cansada porque llevás mucho tiempo sosteniendo más de lo que
              se ve.
            </p>
            <p className="mt-6 text-lg italic" data-id="element-99">
              Como un celular con muchas aplicaciones abiertas: funciona… pero
              la batería se drena igual.
            </p>
          </motion.div>

          <div className="bg-white p-8 rounded-xl shadow-lg relative overflow-hidden" data-id="element-100">
            <div className="absolute top-0 left-0 w-full h-2 bg-[#D4915D]" data-id="element-101"></div>
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2" data-id="element-102">
              <Coffee className="text-[#D4915D]" data-id="element-103" />
              Un espacio para aflojar
            </h3>
            <p className="mb-4" data-id="element-104">
              Este libro nació de escuchar siempre la misma frase, repetida en
              distintas voces:
            </p>
            <blockquote className="border-l-4 border-[#D4915D] pl-4 py-2 my-6 bg-[#F5F1E8] italic text-lg" data-id="element-105">
              "Estoy cansada… pero no puedo parar."
            </blockquote>
            <p className="mb-4" data-id="element-106">
              No necesitaban soluciones mágicas. Necesitaban ser entendidas.
            </p>
            <p className="mb-4" data-id="element-107">
              Por eso esta guía no intenta arreglarte. No intenta cambiarte.
            </p>
            <p className="font-bold text-lg text-center my-6" data-id="element-108">Te acompaña.</p>
            <p data-id="element-109">Para leer despacio. Para volver cuando lo necesites.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4" data-id="element-110">
        <div className="max-w-6xl mx-auto" data-id="element-111">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-112">
            Voces que ya encontraron alivio
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-113">
            <TestimonialCard name="María" age={34} quote="Por fin alguien pone en palabras lo que siento. No necesitaba más consejos, necesitaba que me dijeran que no estoy loca por sentirme así." data-id="element-114" />
            <TestimonialCard name="Laura" age={41} quote="Lloré leyendo las primeras páginas. Sentí que me sacaban una mochila de encima. Es un abrazo en forma de libro." data-id="element-115" />
            <TestimonialCard name="Silvana" age={38} quote="Siempre creí que tenía que esforzarme más para estar bien. Este libro me enseñó que a veces la respuesta es soltar." data-id="element-116" />
            <div className="hidden md:block" data-id="element-117">
              <TestimonialCard name="Andrea" age={29} quote="Es corto, directo al corazón y sin vueltas. Lo tengo en mi mesa de luz y lo abro cuando siento que el día me pasó por encima." data-id="element-118" />
            </div>
            <div className="hidden md:block" data-id="element-119">
              <TestimonialCard name="Patricia" age={45} quote="Me regalé este libro en un momento de colapso. Fue la mejor inversión en mí misma que hice en años." data-id="element-120" />
            </div>
            {/* Mobile-only view more placeholder or just hide extra on mobile to keep clean */}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 px-4 bg-white" data-id="element-121">
        <div className="max-w-5xl mx-auto" data-id="element-122">
          <div className="text-center mb-12" data-id="element-123">
            <span className="bg-[#D4915D] text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-124">
              Gratis por el día de hoy
            </span>
            <h2 className="text-3xl font-bold mt-4 mb-2" data-id="element-125">
              Regalos exclusivos para acompañarte
            </h2>
            <p className="text-gray-600" data-id="element-126">
              Complementos perfectos para tu proceso de alivio
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-127">
            {[{
            img: BONUS_1,
            title: 'Test de Cansancio Emocional',
            desc: 'Entendé y validá cómo te sentís en menos de 5 minutos.'
          }, {
            img: BONUS_2,
            title: 'Ritual de 10 Minutos',
            desc: 'Para aflojar el día sin necesidad de meditar ni esforzarte.'
          }, {
            img: BONUS_3,
            title: 'Guía Volver a Vos',
            desc: 'Pequeños recordatorios para no perderte en la rutina.'
          }].map((bonus, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="flex flex-col h-full" onClick={scrollToPurchase} data-id="element-128">
                <div className="bg-[#F5F1E8] p-4 rounded-xl mb-4 flex-grow flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow" data-id="element-129">
                  <img src={bonus.img} alt={bonus.title} className="rounded-lg shadow-md w-full object-cover" data-id="element-130" />
                </div>
                <h3 className="font-bold text-lg mb-2 text-[#2C2C2C]" data-id="element-131">
                  {bonus.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-132">{bonus.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase-section" className="py-20 px-4 bg-[#F5F1E8] relative overflow-hidden" data-id="element-133">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" data-id="element-134">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#D4915D] blur-3xl" data-id="element-135"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[#D4915D] blur-3xl" data-id="element-136"></div>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-[#E5E0D8] relative z-10" data-id="element-137">
          <div className="grid md:grid-cols-2" data-id="element-138">
            <div className="p-8 bg-[#EAE4D9] flex flex-col justify-center items-center text-center" data-id="element-139">
              <img src={MAIN_IMAGE} alt="Libro completo" className="w-3/4 shadow-2xl rounded-lg mb-6 transform rotate-[-2deg]" data-id="element-140" />
              <div className="space-y-2 text-sm text-gray-600" data-id="element-141">
                <p className="flex items-center justify-center gap-2" data-id="element-142">
                  <BookOpen className="w-4 h-4" data-id="element-143" /> Formato Digital (PDF)
                </p>
                <p className="flex items-center justify-center gap-2" data-id="element-144">
                  <Star className="w-4 h-4" data-id="element-145" /> Acceso Inmediato
                </p>
              </div>
            </div>

            <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-146">
              <h2 className="text-3xl font-bold mb-2" data-id="element-147">
                Cómo Sentirte Mejor Sin Cambiar Nada
              </h2>
              <p className="text-gray-500 mb-6" data-id="element-148">
                El alivio que estás buscando, hoy.
              </p>

              <div className="space-y-4 mb-8" data-id="element-149">
                <div className="flex items-center gap-3" data-id="element-150">
                  <div className="bg-green-100 p-1 rounded-full" data-id="element-151">
                    <Check className="w-4 h-4 text-green-600" data-id="element-152" />
                  </div>
                  <span className="text-sm" data-id="element-153">Libro digital completo</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-154">
                  <div className="bg-green-100 p-1 rounded-full" data-id="element-155">
                    <Check className="w-4 h-4 text-green-600" data-id="element-156" />
                  </div>
                  <span className="text-sm" data-id="element-157">Bonus: Test de Cansancio</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-158">
                  <div className="bg-green-100 p-1 rounded-full" data-id="element-159">
                    <Check className="w-4 h-4 text-green-600" data-id="element-160" />
                  </div>
                  <span className="text-sm" data-id="element-161">Bonus: Ritual de 10 Minutos</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-162">
                  <div className="bg-green-100 p-1 rounded-full" data-id="element-163">
                    <Check className="w-4 h-4 text-green-600" data-id="element-164" />
                  </div>
                  <span className="text-sm" data-id="element-165">Bonus: Guía Volver a Vos</span>
                </div>
              </div>

              <div className="mt-auto" data-id="element-166">
                <div className="flex items-end gap-2 mb-2" data-id="element-167">
                  <span className="text-4xl font-bold text-[#2C2C2C]" data-id="element-168">
                    $19.990
                  </span>
                  <span className="text-gray-400 line-through mb-2 text-sm" data-id="element-169">
                    $35.000
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-6" data-id="element-170">
                  Oferta especial por tiempo limitado
                </p>

                <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={handlePurchase} className="w-full bg-[#D4915D] text-white py-4 rounded-lg font-bold text-xl shadow-lg hover:bg-[#c4824d] transition-colors flex justify-center items-center gap-2" data-id="element-171">
                  DESCARGAR AHORA
                  <ArrowRight className="w-5 h-5" data-id="element-172" />
                </motion.button>

                <p className="text-center text-xs text-gray-400 mt-4 flex items-center justify-center gap-1" data-id="element-173">
                  <span className="w-2 h-2 bg-green-500 rounded-full" data-id="element-174"></span>
                  Pago 100% Seguro
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] text-white py-12 px-4 text-center" data-id="element-175">
        <div className="max-w-2xl mx-auto opacity-80" data-id="element-176">
          <p className="mb-4 text-lg font-serif italic" data-id="element-177">
            "No te empujes, date espacio para volver a vos."
          </p>
          <div className="w-16 h-px bg-white/20 mx-auto my-6" data-id="element-178"></div>
          <p className="text-sm text-gray-400" data-id="element-179">
            © 2024 Cómo Sentirte Mejor Sin Cambiar Nada. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}